import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { API_BASE } from '../utils/apiEndpoints';
import { TheoryTopicsAdminSection } from '../components/admin/TheoryTopicsAdminSection.tsx';
import './css/AdminPage.css';

interface ReportItem {
  id: number;
  reporterId: number;
  reporterUsername: string | null;
  reportedUserId: number;
  reportedUsername: string | null;
  reason: string;
  message: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface BugReportItem {
  id: number;
  userId: number;
  username: string | null;
  problemId: number;
  problemTitle: string | null;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface SuggestionItem {
  id: number;
  userId: number;
  username: string | null;
  title: string;
  difficulty: string;
  topic: string;
  description: string;
  input_format?: string;
  output_format?: string;
  notes?: string;
  constraints: string[];
  examples: Array<{ input?: string; output?: string; explanation?: string }>;
  tags?: string[];
  languages?: string[];
  test_cases?: Array<{ input?: string; output?: string }>;
  starter_code?: Record<string, string>;
  is_premium: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminProblemItem {
  id: number;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  topic: string;
  is_premium: boolean;
  admin_only?: boolean;
  execution_in_place?: boolean;
  is_debug_task?: boolean;
  tags: string[];
  constraints: string[];
  description: string;
  examples: Array<{ input?: string; output?: string; explanation?: string }>;
  test_cases?: unknown[];
  starter_code?: Record<string, unknown>;
  sql_read_only?: boolean;
  solution_code?: Record<string, unknown>;
  title_i18n?: Record<string, unknown> | null;
  description_i18n?: Record<string, unknown> | null;
  constraints_i18n?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

type AdminSection = 'reports' | 'problemBugs' | 'suggestions' | 'problems' | 'theoryTopics' | 'theoryLinks';

interface TheoryLinkItem {
  id: number;
  sort_order: number;
  lesson: { id: number; slug: string; title: string } | null;
  category: { id: number; slug: string; title: string } | null;
  problem: { id: number; title: string; topic: string; difficulty: string } | null;
}

interface TheoryLessonOption {
  id: number;
  slug: string;
  title: string;
  categoryTitle: string;
  categorySlug: string;
}

interface TheoryLinkProblemOption {
  id: number;
  title: string;
  topic: string;
  difficulty: string;
}

const REPORT_STATUSES = ['open', 'in_progress', 'resolved', 'dismissed'] as const;
const SUGGESTION_STATUSES = ['pending', 'reviewed', 'accepted', 'rejected'] as const;

function AdminPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { t, currentLanguage } = useTranslation();
  const [section, setSection] = useState<AdminSection>('reports');
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const [bugReports, setBugReports] = useState<BugReportItem[]>([]);
  const [bugReportsLoading, setBugReportsLoading] = useState(false);
  const [bugReportsError, setBugReportsError] = useState<string | null>(null);
  const [bugStatusFilter, setBugStatusFilter] = useState<string>('');
  const [bugUpdatingId, setBugUpdatingId] = useState<number | null>(null);

  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [suggestionStatusFilter, setSuggestionStatusFilter] = useState<string>('');
  const [suggestionUpdatingId, setSuggestionUpdatingId] = useState<number | null>(null);

  const [problems, setProblems] = useState<AdminProblemItem[]>([]);
  const [problemsLoading, setProblemsLoading] = useState(false);
  const [problemsError, setProblemsError] = useState<string | null>(null);
  const [problemSearch, setProblemSearch] = useState('');
  /** Пустая строка = все темы (SQL, Algorithms и т.д.) */
  const [problemTopicFilter, setProblemTopicFilter] = useState('');
  const [problemDifficultyFilter, setProblemDifficultyFilter] = useState<'' | 'Easy' | 'Medium' | 'Hard'>('');
  /** '' = все, yes = только premium, no = только бесплатные */
  const [problemPremiumFilter, setProblemPremiumFilter] = useState<'' | 'yes' | 'no'>('');
  /** '' = все, yes = черновик (admin_only), no = общедоступные */
  const [problemDraftFilter, setProblemDraftFilter] = useState<'' | 'yes' | 'no'>('');
  const [adminProblemTopics, setAdminProblemTopics] = useState<string[]>([]);
  const [problemPage, setProblemPage] = useState(1);
  const [problemTotalPages, setProblemTotalPages] = useState(1);
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [editingProblem, setEditingProblem] = useState<AdminProblemItem | null>(null);
  const [savingProblem, setSavingProblem] = useState(false);
  const [deletingProblemId, setDeletingProblemId] = useState<number | null>(null);

  const [formProblemJson, setFormProblemJson] = useState('');
  const [loadingProblemJson, setLoadingProblemJson] = useState(false);
  const [formJsonError, setFormJsonError] = useState<string | null>(null);

  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importingProblems, setImportingProblems] = useState(false);
  const [importProblemsError, setImportProblemsError] = useState<string | null>(null);
  const [importProblemsResult, setImportProblemsResult] = useState<{
    created: number;
    failed: number;
    failedItems?: Array<{ index: number; title?: string; errors: string[] }>;
  } | null>(null);

  const [theoryLinks, setTheoryLinks] = useState<TheoryLinkItem[]>([]);
  const [theoryLinksLoading, setTheoryLinksLoading] = useState(false);
  const [theoryLinksError, setTheoryLinksError] = useState<string | null>(null);
  const [theoryLessonOptions, setTheoryLessonOptions] = useState<TheoryLessonOption[]>([]);
  const [theoryLinkLessonId, setTheoryLinkLessonId] = useState('');
  const [theoryLinkProblemId, setTheoryLinkProblemId] = useState('');
  const [theoryLinkSortOrder, setTheoryLinkSortOrder] = useState('0');
  const [theoryLinkProblemSearch, setTheoryLinkProblemSearch] = useState('');
  const [theoryLinkProblemOptions, setTheoryLinkProblemOptions] = useState<TheoryLinkProblemOption[]>([]);
  const [theoryLinkProblemsLoading, setTheoryLinkProblemsLoading] = useState(false);
  const [theoryLinkSaving, setTheoryLinkSaving] = useState(false);
  const [theoryLinkDeletingId, setTheoryLinkDeletingId] = useState<number | null>(null);
  const [theoryLinkFormError, setTheoryLinkFormError] = useState<string | null>(null);
  const [theoryLinkFormSuccess, setTheoryLinkFormSuccess] = useState<string | null>(null);

  const canImportProblems = useMemo(() => user?.role === 'admin' && section === 'problems', [user?.role, section]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (section !== 'reports' || user?.role !== 'admin') return;

    const fetchReports = async () => {
      setReportsLoading(true);
      setReportsError(null);
      try {
        const url = statusFilter
          ? `${API_BASE}/api/reports?status=${encodeURIComponent(statusFilter)}`
          : `${API_BASE}/api/reports`;
        const r = await fetch(url, { credentials: 'include' });
        if (r.status === 403 || r.status === 401) {
          navigate(r.status === 401 ? '/login' : '/', { replace: true });
          return;
        }
        if (!r.ok) throw new Error('Failed to load reports');
        const data = await r.json();
        setReports(Array.isArray(data) ? data : []);
      } catch (e) {
        setReportsError(e instanceof Error ? e.message : t('admin.unknownError'));
      } finally {
        setReportsLoading(false);
      }
    };

    fetchReports();
  }, [section, statusFilter, user?.role, navigate, t]);

  useEffect(() => {
    if (section !== 'problemBugs' || user?.role !== 'admin') return;

    const fetchBugReports = async () => {
      setBugReportsLoading(true);
      setBugReportsError(null);
      try {
        const url = bugStatusFilter
          ? `${API_BASE}/api/problem-bugs?status=${encodeURIComponent(bugStatusFilter)}`
          : `${API_BASE}/api/problem-bugs`;
        const r = await fetch(url, { credentials: 'include' });
        if (r.status === 403 || r.status === 401) {
          navigate(r.status === 401 ? '/login' : '/', { replace: true });
          return;
        }
        if (!r.ok) throw new Error('Failed to load');
        const data = await r.json();
        setBugReports(Array.isArray(data) ? data : []);
      } catch (e) {
        setBugReportsError(e instanceof Error ? e.message : t('admin.unknownError'));
      } finally {
        setBugReportsLoading(false);
      }
    };

    fetchBugReports();
  }, [section, bugStatusFilter, user?.role, navigate, t]);

  useEffect(() => {
    if (section !== 'suggestions' || user?.role !== 'admin') return;

    const fetchSuggestions = async () => {
      setSuggestionsLoading(true);
      setSuggestionsError(null);
      try {
        const url = suggestionStatusFilter
          ? `${API_BASE}/api/problem-suggestions?status=${encodeURIComponent(suggestionStatusFilter)}`
          : `${API_BASE}/api/problem-suggestions`;
        const r = await fetch(url, { credentials: 'include' });
        if (r.status === 403 || r.status === 401) {
          navigate(r.status === 401 ? '/login' : '/', { replace: true });
          return;
        }
        if (!r.ok) throw new Error('Failed to load');
        const data = await r.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch (e) {
        setSuggestionsError(e instanceof Error ? e.message : t('admin.unknownError'));
      } finally {
        setSuggestionsLoading(false);
      }
    };

    fetchSuggestions();
  }, [section, suggestionStatusFilter, user?.role, navigate, t]);

  useEffect(() => {
    if (section !== 'problems' || user?.role !== 'admin') return;
    const loadTopics = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/problems/topics_and_tags`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        setAdminProblemTopics(Array.isArray(data?.topics) ? data.topics : []);
      } catch {
        /* список тем опционален */
      }
    };
    loadTopics();
  }, [section, user?.role]);

  useEffect(() => {
    if (section !== 'problems' || user?.role !== 'admin') return;

    const fetchAdminProblems = async () => {
      setProblemsLoading(true);
      setProblemsError(null);
      try {
        const params = new URLSearchParams();
        params.set('search', problemSearch);
        params.set('page', String(problemPage));
        params.set('limit', '50');
        if (problemTopicFilter) params.set('topic', problemTopicFilter);
        if (problemDifficultyFilter) params.set('difficulty', problemDifficultyFilter);
        if (problemPremiumFilter === 'yes') params.set('premium', 'true');
        if (problemPremiumFilter === 'no') params.set('premium', 'false');
        if (problemDraftFilter === 'yes') params.set('admin_only', 'true');
        if (problemDraftFilter === 'no') params.set('admin_only', 'false');
        const url = `${API_BASE}/api/problems/admin/problems?${params.toString()}`;
        const r = await fetch(url, { credentials: 'include' });
        if (r.status === 403 || r.status === 401) {
          navigate(r.status === 401 ? '/login' : '/', { replace: true });
          return;
        }
        if (!r.ok) throw new Error(t('admin.problemsLoadFailed'));
        const data = await r.json();
        setProblems(Array.isArray(data?.problems) ? data.problems : []);
        setProblemTotalPages(Number(data?.totalPages) || 1);
      } catch (e) {
        setProblemsError(e instanceof Error ? e.message : t('admin.unknownError'));
      } finally {
        setProblemsLoading(false);
      }
    };

    fetchAdminProblems();
  }, [
    section,
    user?.role,
    problemSearch,
    problemTopicFilter,
    problemDifficultyFilter,
    problemPremiumFilter,
    problemDraftFilter,
    problemPage,
    navigate,
    t,
  ]);

  const loadTheoryLinks = async () => {
    setTheoryLinksLoading(true);
    setTheoryLinksError(null);
    try {
      const lang = currentLanguage === 'ru' ? 'ru' : 'en';
      const r = await fetch(`${API_BASE}/api/theory/admin/links?lang=${lang}`, {
        credentials: 'include',
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error(t('admin.theoryLinksLoadFailed'));
      const data = await r.json();
      setTheoryLinks(Array.isArray(data?.links) ? data.links : []);
    } catch (e) {
      setTheoryLinksError(e instanceof Error ? e.message : t('admin.unknownError'));
    } finally {
      setTheoryLinksLoading(false);
    }
  };

  useEffect(() => {
    if (section !== 'theoryLinks' || user?.role !== 'admin') return;

    const loadLessons = async () => {
      try {
        const lang = currentLanguage === 'ru' ? 'ru' : 'en';
        const r = await fetch(`${API_BASE}/api/theory/library?lang=${lang}`, {
          credentials: 'include',
        });
        if (!r.ok) return;
        const data = await r.json();
        const categories = Array.isArray(data?.categories) ? data.categories : [];
        const options: TheoryLessonOption[] = [];
        for (const category of categories) {
          for (const lesson of category.lessons || []) {
            options.push({
              id: lesson.id,
              slug: lesson.slug,
              title: lesson.title,
              categoryTitle: category.title,
              categorySlug: category.slug,
            });
          }
        }
        setTheoryLessonOptions(options);
      } catch {
        setTheoryLessonOptions([]);
      }
    };

    loadTheoryLinks();
    loadLessons();
  }, [section, user?.role, navigate, currentLanguage]);

  useEffect(() => {
    if (section !== 'theoryLinks' || user?.role !== 'admin') return;

    const timer = window.setTimeout(async () => {
      setTheoryLinkProblemsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('search', theoryLinkProblemSearch.trim());
        params.set('page', '1');
        params.set('limit', '50');
        const r = await fetch(`${API_BASE}/api/problems/admin/problems?${params.toString()}`, {
          credentials: 'include',
        });
        if (!r.ok) return;
        const data = await r.json();
        const items = Array.isArray(data?.problems) ? data.problems : [];
        setTheoryLinkProblemOptions(
          items.map((item: AdminProblemItem) => ({
            id: item.id,
            title: item.title,
            topic: item.topic,
            difficulty: item.difficulty,
          }))
        );
      } catch {
        setTheoryLinkProblemOptions([]);
      } finally {
        setTheoryLinkProblemsLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [section, user?.role, theoryLinkProblemSearch]);

  useCloseOnEscape(showProblemModal, () => setShowProblemModal(false));

  const safeJsonParse = (label: string, raw: string) => {
    try {
      return { ok: true as const, value: JSON.parse(raw) };
    } catch (e) {
      return { ok: false as const, error: `${label}: invalid JSON` };
    }
  };

  const problemToEditableJson = (problem: Record<string, unknown>) => {
    const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...editable } = problem;
    return JSON.stringify(editable, null, 2);
  };

  const openEditProblem = async (p: AdminProblemItem) => {
    setEditingProblem(p);
    setFormProblemJson('');
    setFormJsonError(null);
    setShowProblemModal(true);
    setLoadingProblemJson(true);
    try {
      const r = await fetch(`${API_BASE}/api/problems/admin/problems/${p.id}`, { credentials: 'include' });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error(t('admin.problemsLoadFailed'));
      const data = await r.json();
      setEditingProblem({ ...p, ...data });
      setFormProblemJson(problemToEditableJson(data));
    } catch (e) {
      setFormJsonError(e instanceof Error ? e.message : t('admin.problemsLoadFailed'));
      setShowProblemModal(false);
      setEditingProblem(null);
    } finally {
      setLoadingProblemJson(false);
    }
  };

  const saveProblem = async () => {
    if (savingProblem || !editingProblem) return;
    setFormJsonError(null);

    const parsed = safeJsonParse('problem', formProblemJson);
    if (!parsed.ok) return setFormJsonError(parsed.error);
    if (!parsed.value || typeof parsed.value !== 'object' || Array.isArray(parsed.value)) {
      return setFormJsonError('problem: must be a JSON object');
    }

    const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...payload } = parsed.value as Record<
      string,
      unknown
    >;

    setSavingProblem(true);
    try {
      const r = await fetch(`${API_BASE}/api/problems/admin/problems/${editingProblem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = data?.message || t('admin.problemsSaveFailed');
        const details = Array.isArray(data?.errors) ? `: ${data.errors.join(', ')}` : '';
        setFormJsonError(`${msg}${details}`);
        return;
      }
      setShowProblemModal(false);
      setEditingProblem(null);
      setProblemPage((page) => page);
      setProblems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      setFormJsonError(e instanceof Error ? e.message : t('admin.problemsSaveFailed'));
    } finally {
      setSavingProblem(false);
    }
  };

  const deleteProblem = async (id: number) => {
    if (deletingProblemId != null) return;
    const ok = window.confirm(t('admin.problemsDeleteConfirm'));
    if (!ok) return;
    setDeletingProblemId(id);
    try {
      const r = await fetch(`${API_BASE}/api/problems/admin/problems/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) return;
      setProblems((prev) => prev.filter((p) => p.id !== id));
    } finally {
      setDeletingProblemId(null);
    }
  };

  const readFileAsText = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.readAsText(file);
    });

  const normalizeImportedProblems = (parsed: unknown) => {
    // Accept either array or { problems: array } for convenience
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).problems)) return (parsed as any).problems;
    return null;
  };

  const handleImportProblemsFile = async (file: File) => {
    if (importingProblems) return;
    setImportProblemsError(null);
    setImportProblemsResult(null);

    // Basic client-side limit to avoid freezing UI; server has its own limit too.
    const MAX_BYTES = 2 * 1024 * 1024; // 2MB
    if (file.size > MAX_BYTES) {
      setImportProblemsError(`File too large (max ${Math.round(MAX_BYTES / 1024 / 1024)}MB)`);
      return;
    }

    setImportingProblems(true);
    try {
      const text = await readFileAsText(file);
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        setImportProblemsError('Invalid JSON');
        return;
      }

      const problemsArray = normalizeImportedProblems(parsed);
      if (!problemsArray) {
        setImportProblemsError('JSON must be an array of problems or { problems: [...] }');
        return;
      }
      if (problemsArray.length === 0) {
        setImportProblemsError('JSON array is empty');
        return;
      }

      // Импорт всегда создаёт черновики (admin_only), даже если поле не указано в JSON.
      const problemsAsDrafts = problemsArray.map((item) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? { ...(item as Record<string, unknown>), admin_only: true }
          : item,
      );

      const r = await fetch(`${API_BASE}/api/problems/admin/problems/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ problems: problemsAsDrafts }),
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }

      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = data?.message || 'Import failed';
        const details = Array.isArray(data?.errors) ? `: ${data.errors.join(', ')}` : '';
        setImportProblemsError(`${msg}${details}`);
        if (Array.isArray(data?.failed)) {
          setImportProblemsResult({
            created: Array.isArray(data?.created) ? data.created.length : 0,
            failed: data.failed.length,
            failedItems: data.failed.slice(0, 20),
          });
        }
        return;
      }

      const createdCount = Array.isArray(data?.created) ? data.created.length : 0;
      const failedArr = Array.isArray(data?.failed) ? data.failed : [];
      setImportProblemsResult({
        created: createdCount,
        failed: failedArr.length,
        failedItems: failedArr.slice(0, 20),
      });

      // refresh current page
      setProblemPage((p) => p);
    } catch (e) {
      setImportProblemsError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImportingProblems(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const handleStatusChange = async (reportId: number, newStatus: string) => {
    if (!REPORT_STATUSES.includes(newStatus as any)) return;
    setUpdatingId(reportId);
    try {
      const r = await fetch(`${API_BASE}/api/reports/${reportId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error('Update failed');
      setReports((prev) =>
        prev.map((item) =>
          item.id === reportId ? { ...item, status: newStatus } : item
        )
      );
    } catch {
      // keep current state
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBugStatusChange = async (reportId: number, newStatus: string) => {
    if (!REPORT_STATUSES.includes(newStatus as any)) return;
    setBugUpdatingId(reportId);
    try {
      const r = await fetch(`${API_BASE}/api/problem-bugs/${reportId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error('Update failed');
      setBugReports((prev) =>
        prev.map((item) =>
          item.id === reportId ? { ...item, status: newStatus } : item
        )
      );
    } catch {
      // keep current state
    } finally {
      setBugUpdatingId(null);
    }
  };

  const handleSuggestionStatusChange = async (id: number, newStatus: string) => {
    if (!SUGGESTION_STATUSES.includes(newStatus as any)) return;
    setSuggestionUpdatingId(id);
    try {
      const r = await fetch(`${API_BASE}/api/problem-suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error('Update failed');
      setSuggestions((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
      );
    } catch {
      // keep current state
    } finally {
      setSuggestionUpdatingId(null);
    }
  };

  const reasonLabel = (reason: string) => {
    const key = `admin.reportReason.${reason}`;
    const label = t(key);
    return label !== key ? label : reason;
  };

  const statusLabel = (status: string) => {
    const key = `admin.reportStatus.${status}`;
    const label = t(key);
    return label !== key ? label : status;
  };

  const suggestionStatusLabel = (status: string) => {
    const key = `admin.suggestionStatus.${status}`;
    const label = t(key);
    return label !== key ? label : status;
  };

  const adminDifficultyLabel = (d: string) => {
    if (d === 'Easy') return t('problemsList.difficultyEasy');
    if (d === 'Medium') return t('problemsList.difficultyMedium');
    if (d === 'Hard') return t('problemsList.difficultyHard');
    return d;
  };

  const addTheoryLink = async () => {
    setTheoryLinkFormError(null);
    setTheoryLinkFormSuccess(null);

    const lessonId = Number(theoryLinkLessonId);
    const problemId = Number(theoryLinkProblemId);
    const sortOrder = Number(theoryLinkSortOrder);

    if (!Number.isFinite(lessonId) || !Number.isFinite(problemId)) {
      setTheoryLinkFormError(t('admin.theoryLinksFormInvalid'));
      return;
    }

    setTheoryLinkSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/theory/admin/links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          theory_lesson_id: lessonId,
          problem_id: problemId,
          sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
        }),
      });

      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }

      if (r.status === 409) {
        setTheoryLinkFormError(t('admin.theoryLinksDuplicate'));
        return;
      }

      if (!r.ok) {
        throw new Error(t('admin.theoryLinksAddFailed'));
      }

      setTheoryLinkFormSuccess(t('admin.theoryLinksAdded'));
      setTheoryLinkLessonId('');
      setTheoryLinkProblemId('');
      setTheoryLinkSortOrder('0');
      await loadTheoryLinks();
    } catch (e) {
      setTheoryLinkFormError(e instanceof Error ? e.message : t('admin.theoryLinksAddFailed'));
    } finally {
      setTheoryLinkSaving(false);
    }
  };

  const deleteTheoryLink = async (linkId: number) => {
    if (!window.confirm(t('admin.theoryLinksDeleteConfirm'))) return;

    setTheoryLinkDeletingId(linkId);
    try {
      const r = await fetch(`${API_BASE}/api/theory/admin/links/${linkId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (r.status === 403 || r.status === 401) {
        navigate(r.status === 401 ? '/login' : '/', { replace: true });
        return;
      }
      if (!r.ok) throw new Error(t('admin.theoryLinksDeleteFailed'));
      setTheoryLinks((prev) => prev.filter((item) => item.id !== linkId));
    } catch {
      setTheoryLinksError(t('admin.theoryLinksDeleteFailed'));
    } finally {
      setTheoryLinkDeletingId(null);
    }
  };

  if (authLoading || !user) {
    return <div className="admin-loading">{t('admin.loading')}</div>;
  }
  if (user.role !== 'admin') {
    return null;
  }

  return (
    <div className="admin-page">
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <h2 className="admin-sidebar-title">{t('admin.title')}</h2>
          <nav className="admin-nav">
            <button
              type="button"
              className={`admin-nav-item ${section === 'reports' ? 'active' : ''}`}
              onClick={() => setSection('reports')}
            >
              {t('admin.reports')}
            </button>
            <button
              type="button"
              className={`admin-nav-item ${section === 'problemBugs' ? 'active' : ''}`}
              onClick={() => setSection('problemBugs')}
            >
              {t('admin.problemBugs')}
            </button>
            <button
              type="button"
              className={`admin-nav-item ${section === 'suggestions' ? 'active' : ''}`}
              onClick={() => setSection('suggestions')}
            >
              {t('admin.suggestions')}
            </button>
            <button
              type="button"
              className={`admin-nav-item ${section === 'problems' ? 'active' : ''}`}
              onClick={() => setSection('problems')}
            >
              {t('admin.problems')}
            </button>
            <button
              type="button"
              className={`admin-nav-item ${section === 'theoryTopics' ? 'active' : ''}`}
              onClick={() => setSection('theoryTopics')}
            >
              {t('admin.theoryTopics')}
            </button>
            <button
              type="button"
              className={`admin-nav-item ${section === 'theoryLinks' ? 'active' : ''}`}
              onClick={() => setSection('theoryLinks')}
            >
              {t('admin.theoryLinks')}
            </button>
          </nav>
        </aside>

        <main className="admin-main">
          {section === 'reports' && (
            <div className="admin-reports">
              <div className="admin-reports-header">
                <h1 className="admin-reports-title">{t('admin.reports')}</h1>
                <div className="admin-reports-filters">
                  <label htmlFor="admin-status-filter" className="admin-filter-label">
                    {t('admin.filterByStatus')}
                  </label>
                  <select
                    id="admin-status-filter"
                    className="admin-filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">{t('admin.allStatuses')}</option>
                    {REPORT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {statusLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {reportsError && (
                <div className="admin-error" role="alert">
                  {reportsError}
                </div>
              )}

              {reportsLoading ? (
                <div className="admin-loading-inline">{t('admin.loadingReports')}</div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>{t('admin.reportId')}</th>
                        <th>{t('admin.reporter')}</th>
                        <th>{t('admin.reportedUser')}</th>
                        <th>{t('admin.reason')}</th>
                        <th>{t('admin.message')}</th>
                        <th>{t('admin.status')}</th>
                        <th>{t('admin.date')}</th>
                        <th>{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="admin-table-empty">
                            {t('admin.noReports')}
                          </td>
                        </tr>
                      ) : (
                        reports.map((item) => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>
                              <span title={String(item.reporterId)}>
                                {item.reporterUsername ?? '—'}
                              </span>
                            </td>
                            <td>
                              <LinkToProfile id={item.reportedUserId} username={item.reportedUsername} />
                            </td>
                            <td>{reasonLabel(item.reason)}</td>
                            <td className="admin-cell-message">
                              {item.message ? (
                                <span title={item.message}>
                                  {item.message.length > 80
                                    ? `${item.message.slice(0, 80)}…`
                                    : item.message}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <span className={`admin-status-badge admin-status-${item.status}`}>
                                {statusLabel(item.status)}
                              </span>
                            </td>
                            <td>
                              {item.createdAt
                                ? new Date(item.createdAt).toLocaleString()
                                : '—'}
                            </td>
                            <td>
                              <select
                                className="admin-status-select"
                                value={item.status}
                                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                disabled={updatingId === item.id}
                              >
                                {REPORT_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {statusLabel(s)}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {section === 'problemBugs' && (
            <div className="admin-reports">
              <div className="admin-reports-header">
                <h1 className="admin-reports-title">{t('admin.problemBugs')}</h1>
                <div className="admin-reports-filters">
                  <label htmlFor="admin-bug-status-filter" className="admin-filter-label">
                    {t('admin.filterByStatus')}
                  </label>
                  <select
                    id="admin-bug-status-filter"
                    className="admin-filter-select"
                    value={bugStatusFilter}
                    onChange={(e) => setBugStatusFilter(e.target.value)}
                  >
                    <option value="">{t('admin.allStatuses')}</option>
                    {REPORT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {statusLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {bugReportsError && (
                <div className="admin-error" role="alert">
                  {bugReportsError}
                </div>
              )}

              {bugReportsLoading ? (
                <div className="admin-loading-inline">{t('admin.loadingBugReports')}</div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>{t('admin.bugId')}</th>
                        <th>{t('admin.bugUser')}</th>
                        <th>{t('admin.bugProblem')}</th>
                        <th>{t('admin.bugDescription')}</th>
                        <th>{t('admin.status')}</th>
                        <th>{t('admin.date')}</th>
                        <th>{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bugReports.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="admin-table-empty">
                            {t('admin.noBugReports')}
                          </td>
                        </tr>
                      ) : (
                        bugReports.map((item) => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>
                              <span title={String(item.userId)}>
                                {item.username ?? '—'}
                              </span>
                            </td>
                            <td>
                              <LinkToProblem problemId={item.problemId} title={item.problemTitle} />
                            </td>
                            <td className="admin-cell-message">
                              {item.description ? (
                                <span title={item.description}>
                                  {item.description.length > 80
                                    ? `${item.description.slice(0, 80)}…`
                                    : item.description}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <span className={`admin-status-badge admin-status-${item.status}`}>
                                {statusLabel(item.status)}
                              </span>
                            </td>
                            <td>
                              {item.createdAt
                                ? new Date(item.createdAt).toLocaleString()
                                : '—'}
                            </td>
                            <td>
                              <select
                                className="admin-status-select"
                                value={item.status}
                                onChange={(e) => handleBugStatusChange(item.id, e.target.value)}
                                disabled={bugUpdatingId === item.id}
                              >
                                {REPORT_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {statusLabel(s)}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {section === 'suggestions' && (
            <div className="admin-reports">
              <div className="admin-reports-header">
                <h1 className="admin-reports-title">{t('admin.suggestions')}</h1>
                <div className="admin-reports-filters">
                  <label htmlFor="admin-suggestion-status-filter" className="admin-filter-label">
                    {t('admin.filterByStatus')}
                  </label>
                  <select
                    id="admin-suggestion-status-filter"
                    className="admin-filter-select"
                    value={suggestionStatusFilter}
                    onChange={(e) => setSuggestionStatusFilter(e.target.value)}
                  >
                    <option value="">{t('admin.allStatuses')}</option>
                    {SUGGESTION_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {suggestionStatusLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {suggestionsError && (
                <div className="admin-error" role="alert">
                  {suggestionsError}
                </div>
              )}

              {suggestionsLoading ? (
                <div className="admin-loading-inline">{t('admin.loadingSuggestions')}</div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>{t('admin.suggestionId')}</th>
                        <th>{t('admin.suggestionUser')}</th>
                        <th>{t('admin.suggestionTitle')}</th>
                        <th>{t('admin.suggestionDifficulty')}</th>
                        <th>{t('admin.suggestionTopic')}</th>
                        <th>{t('admin.suggestionStatus')}</th>
                        <th>{t('admin.date')}</th>
                        <th>{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="admin-table-empty">
                            {t('admin.noSuggestions')}
                          </td>
                        </tr>
                      ) : (
                        suggestions.map((item) => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>
                              <span title={String(item.userId)}>{item.username ?? '—'}</span>
                            </td>
                            <td className="admin-cell-message">
                              <span title={item.description}>
                                {item.title.length > 40 ? `${item.title.slice(0, 40)}…` : item.title}
                              </span>
                            </td>
                            <td>{item.difficulty}</td>
                            <td>{item.topic}</td>
                            <td>
                              <span className={`admin-status-badge admin-status-${item.status}`}>
                                {suggestionStatusLabel(item.status)}
                              </span>
                            </td>
                            <td>
                              {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                            </td>
                            <td>
                              <select
                                className="admin-status-select"
                                value={item.status}
                                onChange={(e) => handleSuggestionStatusChange(item.id, e.target.value)}
                                disabled={suggestionUpdatingId === item.id}
                              >
                                {SUGGESTION_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {suggestionStatusLabel(s)}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {section === 'problems' && (
            <div className="admin-reports">
              <div className="admin-reports-header">
                <h1 className="admin-reports-title">{t('admin.problems')}</h1>
                <div className="admin-problems-toolbar">
                  <select
                    id="admin-problem-topic-filter"
                    className="admin-filter-select admin-problems-topic-select"
                    value={problemTopicFilter}
                    onChange={(e) => {
                      setProblemTopicFilter(e.target.value);
                      setProblemPage(1);
                    }}
                    aria-label={t('admin.problemsFilterTopicAria')}
                  >
                    <option value="">{t('admin.problemsFilterTopicAll')}</option>
                    {adminProblemTopics.map((tp) => (
                      <option key={tp} value={tp}>
                        {tp}
                      </option>
                    ))}
                  </select>
                  <select
                    id="admin-problem-difficulty-filter"
                    className="admin-filter-select admin-problems-topic-select"
                    value={problemDifficultyFilter}
                    onChange={(e) => {
                      setProblemDifficultyFilter(e.target.value as '' | 'Easy' | 'Medium' | 'Hard');
                      setProblemPage(1);
                    }}
                    aria-label={t('admin.problemsFilterDifficultyAria')}
                  >
                    <option value="">{t('admin.problemsFilterDifficultyAll')}</option>
                    <option value="Easy">{t('problemsList.difficultyEasy')}</option>
                    <option value="Medium">{t('problemsList.difficultyMedium')}</option>
                    <option value="Hard">{t('problemsList.difficultyHard')}</option>
                  </select>
                  <select
                    id="admin-problem-premium-filter"
                    className="admin-filter-select admin-problems-topic-select"
                    value={problemPremiumFilter}
                    onChange={(e) => {
                      setProblemPremiumFilter(e.target.value as '' | 'yes' | 'no');
                      setProblemPage(1);
                    }}
                    aria-label={t('admin.problemsFilterPremiumAria')}
                  >
                    <option value="">{t('admin.problemsFilterPremiumAll')}</option>
                    <option value="yes">{t('admin.problemsFilterPremiumYes')}</option>
                    <option value="no">{t('admin.problemsFilterPremiumNo')}</option>
                  </select>
                  <select
                    id="admin-problem-draft-filter"
                    className="admin-filter-select admin-problems-topic-select"
                    value={problemDraftFilter}
                    onChange={(e) => {
                      setProblemDraftFilter(e.target.value as '' | 'yes' | 'no');
                      setProblemPage(1);
                    }}
                    aria-label={t('admin.problemsFilterDraftAria')}
                  >
                    <option value="">{t('admin.problemsFilterDraftAll')}</option>
                    <option value="yes">{t('admin.problemsFilterDraftYes')}</option>
                    <option value="no">{t('admin.problemsFilterDraftNo')}</option>
                  </select>
                  <input
                    className="admin-problems-search"
                    value={problemSearch}
                    onChange={(e) => {
                      setProblemSearch(e.target.value);
                      setProblemPage(1);
                    }}
                    placeholder={t('admin.problemsSearchPlaceholder')}
                  />
                  <button
                    type="button"
                    className="admin-secondary-btn"
                    onClick={() => {
                      setImportProblemsError(null);
                      setImportProblemsResult(null);
                      importInputRef.current?.click();
                    }}
                    disabled={!canImportProblems || importingProblems}
                    title="Import problems from JSON"
                  >
                    {importingProblems ? 'Importing…' : 'Import JSON'}
                  </button>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="application/json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleImportProblemsFile(f);
                    }}
                  />
                </div>
              </div>

              {problemsError && (
                <div className="admin-error" role="alert">
                  {problemsError}
                </div>
              )}
              {importProblemsError && (
                <div className="admin-error" role="alert">
                  {importProblemsError}
                </div>
              )}
              {importProblemsResult && (
                <div className="admin-success" role="status">
                  <div>
                    Imported: created {importProblemsResult.created}, failed {importProblemsResult.failed}
                  </div>
                  {importProblemsResult.failedItems && importProblemsResult.failedItems.length > 0 && (
                    <div className="admin-cell-message" style={{ marginTop: 6 }}>
                      {importProblemsResult.failedItems.map((it) => (
                        <div key={`${it.index}-${it.title || ''}`}>
                          #{it.index}
                          {it.title ? ` (${it.title})` : ''}: {Array.isArray(it.errors) ? it.errors.join(', ') : 'error'}
                        </div>
                      ))}
                      {importProblemsResult.failed > importProblemsResult.failedItems.length && (
                        <div>…and more</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {problemsLoading ? (
                <div className="admin-loading-inline">{t('admin.problemsLoading')}</div>
              ) : (
                <>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>{t('admin.problemsColId')}</th>
                          <th>{t('admin.problemsColTitle')}</th>
                          <th>{t('admin.problemsColTopic')}</th>
                          <th>{t('admin.problemsColDifficulty')}</th>
                          <th>{t('admin.problemsColPremium')}</th>
                          <th>{t('admin.problemsColAdminOnly')}</th>
                          <th>{t('admin.problemsColTags')}</th>
                          <th>{t('admin.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {problems.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="admin-table-empty">
                              {t('admin.problemsEmpty')}
                            </td>
                          </tr>
                        ) : (
                          problems.map((p) => (
                            <tr key={p.id}>
                              <td>{p.id}</td>
                              <td className="admin-cell-message">
                                <button
                                  type="button"
                                  className="admin-link-profile"
                                  onClick={() => navigate(`/problems/${p.id}`)}
                                >
                                  {p.title}
                                </button>
                              </td>
                              <td>{p.topic}</td>
                              <td>{adminDifficultyLabel(p.difficulty)}</td>
                              <td>{p.is_premium ? t('admin.problemsPremiumYes') : t('admin.problemsPremiumNo')}</td>
                              <td>{p.admin_only ? t('admin.problemsAdminOnlyYes') : t('admin.problemsAdminOnlyNo')}</td>
                              <td className="admin-cell-message">{(p.tags || []).slice(0, 6).join(', ')}</td>
                              <td>
                                <div className="admin-problems-actions">
                                  <button type="button" className="admin-secondary-btn" onClick={() => openEditProblem(p)}>
                                    {t('admin.problemsEdit')}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-danger-btn"
                                    onClick={() => deleteProblem(p.id)}
                                    disabled={deletingProblemId === p.id}
                                  >
                                    {t('admin.problemsDelete')}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="admin-pagination">
                    <button
                      type="button"
                      className="admin-secondary-btn"
                      disabled={problemPage <= 1}
                      onClick={() => setProblemPage((p) => Math.max(1, p - 1))}
                    >
                      {t('admin.problemsPaginationBack')}
                    </button>
                    <div className="admin-pagination-meta">
                      {problemPage} / {problemTotalPages}
                    </div>
                    <button
                      type="button"
                      className="admin-secondary-btn"
                      disabled={problemPage >= problemTotalPages}
                      onClick={() => setProblemPage((p) => Math.min(problemTotalPages, p + 1))}
                    >
                      {t('admin.problemsPaginationNext')}
                    </button>
                  </div>
                </>
              )}

              {showProblemModal && editingProblem && (
                <div
                  className="admin-modal-overlay"
                  role="dialog"
                  aria-modal="true"
                  onClick={() => setShowProblemModal(false)}
                >
                  <div className="admin-modal admin-modal-json" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-modal-header">
                      <h3 className="admin-modal-title">
                        {`${t('admin.problemsModalEdit')} #${editingProblem.id}`}
                      </h3>
                      <button
                        type="button"
                        className="admin-modal-close"
                        onClick={() => setShowProblemModal(false)}
                        aria-label={t('admin.problemsModalCloseAria')}
                      >
                        ×
                      </button>
                    </div>
                    <div className="admin-modal-body">
                      {formJsonError && (
                        <div className="admin-error" role="alert">
                          {formJsonError}
                        </div>
                      )}

                      <label className="admin-field admin-field-wide">
                        <span className="admin-field-label">JSON</span>
                        {loadingProblemJson ? (
                          <div className="admin-loading-inline">{t('admin.loading')}</div>
                        ) : (
                          <textarea
                            className="admin-problem-json-editor"
                            spellCheck={false}
                            value={formProblemJson}
                            onChange={(e) => setFormProblemJson(e.target.value)}
                          />
                        )}
                      </label>
                    </div>
                    <div className="admin-modal-footer">
                      <button type="button" className="admin-secondary-btn" onClick={() => setShowProblemModal(false)}>
                        {t('admin.problemsFormCancel')}
                      </button>
                      <button
                        type="button"
                        className="admin-primary-btn"
                        onClick={saveProblem}
                        disabled={savingProblem || loadingProblemJson}
                      >
                        {savingProblem ? t('admin.problemsFormSaving') : t('admin.problemsFormSave')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {section === 'theoryTopics' && <TheoryTopicsAdminSection />}

          {section === 'theoryLinks' && (
            <div className="admin-theory-links">
              <div className="admin-reports-header">
                <h1 className="admin-reports-title">{t('admin.theoryLinksTitle')}</h1>
              </div>

              <div className="admin-theory-links-form">
                <label className="admin-field">
                  <span className="admin-field-label">{t('admin.theoryLinksLesson')}</span>
                  <select
                    className="admin-filter-select admin-theory-links-select"
                    value={theoryLinkLessonId}
                    onChange={(e) => setTheoryLinkLessonId(e.target.value)}
                  >
                    <option value="">{t('admin.theoryLinksLessonPlaceholder')}</option>
                    {theoryLessonOptions.map((lesson) => (
                      <option key={lesson.id} value={lesson.id}>
                        {`${lesson.categoryTitle} → ${lesson.title}`}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-field">
                  <span className="admin-field-label">{t('admin.theoryLinksProblemSearch')}</span>
                  <input
                    type="search"
                    value={theoryLinkProblemSearch}
                    onChange={(e) => setTheoryLinkProblemSearch(e.target.value)}
                    placeholder={t('admin.theoryLinksProblemSearchPlaceholder')}
                  />
                </label>

                <label className="admin-field">
                  <span className="admin-field-label">{t('admin.theoryLinksProblem')}</span>
                  <select
                    className="admin-filter-select admin-theory-links-select"
                    value={theoryLinkProblemId}
                    onChange={(e) => setTheoryLinkProblemId(e.target.value)}
                    disabled={theoryLinkProblemsLoading}
                  >
                    <option value="">{t('admin.theoryLinksProblemPlaceholder')}</option>
                    {theoryLinkProblemOptions.map((problem) => (
                      <option key={problem.id} value={problem.id}>
                        {`#${problem.id} · ${problem.title} (${problem.topic}, ${adminDifficultyLabel(problem.difficulty)})`}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-field admin-field-narrow">
                  <span className="admin-field-label">{t('admin.theoryLinksSort')}</span>
                  <input
                    type="number"
                    value={theoryLinkSortOrder}
                    onChange={(e) => setTheoryLinkSortOrder(e.target.value)}
                    min={0}
                  />
                </label>

                <button
                  type="button"
                  className="admin-primary-btn admin-theory-links-add-btn"
                  onClick={addTheoryLink}
                  disabled={theoryLinkSaving}
                >
                  {theoryLinkSaving ? t('admin.theoryLinksSaving') : t('admin.theoryLinksAdd')}
                </button>
              </div>

              {theoryLinkFormError && (
                <div className="admin-error" role="alert">
                  {theoryLinkFormError}
                </div>
              )}
              {theoryLinkFormSuccess && (
                <div className="admin-success" role="status">
                  {theoryLinkFormSuccess}
                </div>
              )}
              {theoryLinksError && (
                <div className="admin-error" role="alert">
                  {theoryLinksError}
                </div>
              )}

              {theoryLinksLoading ? (
                <div className="admin-loading-inline">{t('admin.theoryLinksLoading')}</div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>{t('admin.theoryLinksColId')}</th>
                        <th>{t('admin.theoryLinksColCategory')}</th>
                        <th>{t('admin.theoryLinksColLesson')}</th>
                        <th>{t('admin.theoryLinksColProblem')}</th>
                        <th>{t('admin.theoryLinksColSort')}</th>
                        <th>{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {theoryLinks.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="admin-table-empty">
                            {t('admin.theoryLinksEmpty')}
                          </td>
                        </tr>
                      ) : (
                        theoryLinks.map((link) => (
                          <tr key={link.id}>
                            <td>{link.id}</td>
                            <td>{link.category?.title ?? '—'}</td>
                            <td>{link.lesson?.title ?? '—'}</td>
                            <td>
                              {link.problem ? (
                                <LinkToProblem
                                  problemId={link.problem.id}
                                  title={link.problem.title}
                                />
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>{link.sort_order}</td>
                            <td>
                              <button
                                type="button"
                                className="admin-danger-btn"
                                onClick={() => deleteTheoryLink(link.id)}
                                disabled={theoryLinkDeletingId === link.id}
                              >
                                {theoryLinkDeletingId === link.id
                                  ? t('admin.theoryLinksDeleting')
                                  : t('admin.theoryLinksDelete')}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function LinkToProfile({
  id,
  username,
}: {
  id: number;
  username: string | null;
}) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="admin-link-profile"
      onClick={() => navigate(`/profile/${id}`)}
    >
      {username ?? id}
    </button>
  );
}

function LinkToProblem({ problemId, title }: { problemId: number; title: string | null }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="admin-link-profile"
      onClick={() => navigate(`/problems/${problemId}`)}
    >
      {title || `#${problemId}`}
    </button>
  );
}

export default AdminPage;
